import React from "react";
import { Box } from "@mui/material";
import {
  Achievement,
  Footer,
  Intro,
  OrganisationMark,
  RecipientName,
  Title,
  type CertificateDesign,
} from "./certificateParts";
import type { CertificateOrientation } from "../../../domain/certificates/certificateTemplates";

/**
 * One corner's diagonal bands: a solid block of the primary colour, an accent
 * stripe beside it and a faint thin stripe beyond, each running across the
 * corner. Drawn for the top-right corner; flipped for the bottom-left.
 */
const CornerBands: React.FC<{
  primary: string;
  accent: string;
  /** How far the outermost stripe reaches from the corner along each edge. */
  reach: number;
  corner: "top-right" | "bottom-left";
}> = ({ primary, accent, reach, corner }) => {
  // A stripe between two distances from the corner, measured along each edge.
  const stripe = (from: number, to: number) =>
    `${reach - to},0 ${reach - from},0 ${reach},${from} ${reach},${to}`;
  return (
    <Box
      component="svg"
      viewBox={`0 0 ${reach} ${reach}`}
      aria-hidden="true"
      sx={{
        position: "absolute",
        width: reach,
        height: reach,
        ...(corner === "top-right"
          ? { top: 0, right: 0 }
          : { bottom: 0, left: 0, transform: "rotate(180deg)" }),
      }}
    >
      <polygon
        points={`${reach * 0.3},0 ${reach},0 ${reach},${reach * 0.7}`}
        fill={primary}
      />
      <polygon points={stripe(reach * 0.78, reach * 0.88)} fill={accent} />
      <polygon
        points={stripe(reach * 0.94, reach)}
        fill={primary}
        opacity={0.35}
      />
    </Box>
  );
};

/**
 * How far each corner's bands reach, and the text column's inset, which keeps
 * clear of them: at each corner the two insets add up to at least 16px more
 * than the reach.
 * Portrait pages are narrower, so their bands are smaller.
 */
const BANDS: Record<
  CertificateOrientation,
  { topRight: number; bottomLeft: number; textInset: string }
> = {
  landscape: {
    topRight: 200,
    bottomLeft: 150,
    textInset: "80px 150px 64px 104px",
  },
  portrait: {
    topRight: 150,
    bottomLeft: 120,
    textInset: "80px 96px 72px 64px",
  },
};

/**
 * Modern: diagonal colour bands across two corners and a large light title,
 * like a course certificate. The text runs left-aligned between the bands.
 */
export const modernDesign: CertificateDesign = {
  bodyFont: "montserrat",
  Layout: ({ template, text, orientation }) => {
    const { primaryColour: primary, accentColour: accent } = template;
    const bands = BANDS[orientation];
    return (
      <>
        <CornerBands
          primary={primary}
          accent={accent}
          reach={bands.topRight}
          corner="top-right"
        />
        <CornerBands
          primary={primary}
          accent={accent}
          reach={bands.bottomLeft}
          corner="bottom-left"
        />
        <Box
          sx={{
            position: "absolute",
            inset: bands.textInset,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            textAlign: "left",
          }}
        >
          <OrganisationMark template={template} align="left" />
          <Title template={template} title={text.certificateTitle} light />
          <Box sx={{ width: 80, height: 6, bgcolor: accent, mb: "24px" }} />
          <Intro template={template} />
          <RecipientName
            template={template}
            name={text.recipientName}
            align="left"
          />
          <Achievement
            template={template}
            text={text}
            orientation={orientation}
          />
          <Box sx={{ flex: 1 }} />
          <Footer template={template} text={text} orientation={orientation} />
        </Box>
      </>
    );
  },
};
